import React, { useContext, useState, useEffect } from "react";
import _ from "lodash";
import LightboxImage from "./LightboxImage";
import { EventGalleryContext } from "./index";
import { FindPhotosGalleryContext } from "../MasterImageSearch/components/results/PhotosContainer";
import { Photo } from "./types";
import { addParamToURL, withCartIdParam } from "../../utils/BrowserHelper";
import Telemetry from "../../utils/rudderstackAnalytics";
// @ts-ignore
import ahoy from "ahoy.js";
import ajax from "superagent";
import TagsSection from "../BrowseEvent/components/albums/TagsSections";
import LightBoxVideo from "./LightBoxVideo";
import { precachePhoto } from "../../utils/PrecacheImage";
import EmailPopup from "./EmailPopup";
import { gssErrorLog, gssLog } from "../../utils/gssLogger";
import DownloadWithEmailModal from "../BrowseEvent/components/albums/DownloadWithEmailModal";
import LightBoxHeader from "./LightBoxHeader";
import LightBoxFooter from "./LightBoxFooter";
import PrintLightboxHeader from "./PrintLightboxHeader";
import ContrastForegroundUtility from "../../utils/ContrastForegroundUtility";
import LightBoxPrintProducts from "../BrowseEvent/components/albums/LightBoxPrintProducts";

interface Video {
  videoContent: boolean;
  videoMuxPlaybackId: string;
  videoStartTime: string;
  hidden: boolean;
  id: number;
}

const LightBox = ({
  photos,
  photoIndex,
  toggleLightBox,
  trackTelemetry = false,
  parentName,
  isAuthorizedForDirectDownloads,
  userId,
  primaryColor,
  curationApiCalled,
  setCurationApiCalled,
  showFavIcon,
  canShowGuestEmailModal,
  windowingProps,
  securedOrgView,
  registerGuestUserAPI,
  setGuestUserId,
  eventClickable,
  secureAccessToken
}: {
  photos: Photo[];
  photoIndex: number;
  toggleLightBox: (index: number) => void;
  trackTelemetry: boolean;
  parentName?: string;
  isAuthorizedForDirectDownloads: boolean;
  userId?: number;
  primaryColor?: string;
  curationApiCalled: boolean;
  setCurationApiCalled: Function;
  showFavIcon?: boolean;
  canShowGuestEmailModal: (photo: any) => void;
  windowingProps?: {
    hasMore: boolean;
    isLoading: boolean;
    loadMore: () => void;
  };
  securedOrgView?: boolean;
  registerGuestUserAPI: string;
  setGuestUserId: (userId: number) => void;
  eventClickable?: boolean;
  secureAccessToken?: string;
}) => {
  const [photoSaved, setPhotoSaved] = useState(false);
  const getPhoto = photos[photoIndex];
  const [defaultHidden, setDefaultHidden] = useState(getPhoto.hidden);
  const [mediaTags, setMediaTags] = useState<
    Array<{
      name: string;
      tagId: number;
      taggableType: string;
    }>
  >([]);
  const eventGalleryContext = useContext(EventGalleryContext);
  const findPhotosContext = useContext(FindPhotosGalleryContext);
  const cartId = eventGalleryContext.cart
    ? eventGalleryContext.cart.id
    : findPhotosContext.cart
    ? findPhotosContext.cart.id
    : null;
  const isEmbed = eventGalleryContext.isEmbed
    ? eventGalleryContext.isEmbed
    : findPhotosContext.isEmbed;

  const showPrintLightboxHeader =
    eventGalleryContext.event?.hasPrintProducts ?? false;

  const [liked, setLiked] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [showDownloadWithEmailModal, setshowDownloadWithEmailModal] =
    useState(false);
  const resetLightBoxImage = async (index: number) => {
    // This is where it switches to next/previous image

    // This is the case when user clicks next/previous while
    // the next batch of photos are still loading (after fetchNextBatchOfPhotos)
    if (window.gss?.albumV2Enabled) {
      if (windowingProps?.isLoading) {
        // Don't do anything. Let the lightbox remain open
        // When the user taps next/previous again, and loading is done,
        // the toggleLightBox will be called as usual
        return;
      }
    }

    toggleLightBox(index);
  };

  const fetchNextBatchOfPhotos = async () => {
    if (!window.gss?.albumV2Enabled) return;
    if (windowingProps?.hasMore) {
      gssLog("LightBox: Fetching next batch of photos");
      // The parent component handles the errors
      windowingProps.loadMore();
    }
  };

  const canDownloadPhotos = () =>
    eventGalleryContext.isAuthorizedForDirectDownloads ||
    isAuthorizedForDirectDownloads;

  const hasPrevImage = () => {
    return !!photos[photoIndex - 1];
  };
  const hasNextImage = () => {
    if (window.gss?.albumV2Enabled) {
      return !!photos[photoIndex + 1] || windowingProps?.hasMore;
    }
    return !!photos[photoIndex + 1];
  };

  const collectEmailModal = () => {
    setshowDownloadWithEmailModal(!showDownloadWithEmailModal);
  };

  const addToCart = (photo: Photo) => {
    if (eventGalleryContext.printProductsflow?.()) {
      return;
    }

    if (eventGalleryContext.canShowEmailPopup()) {
      canShowGuestEmailModal(photo);
    } else if (eventGalleryContext.event) {
      eventGalleryContext.addPhotoToCart(photo);
    } else {
      // @ts-ignore
      findPhotosContext.addPhotoToCart(photo);
    }
  };

  const removeFromCart = (photo: Photo) => {
    if (eventGalleryContext.printProductsflow?.()) {
      return;
    }

    if (eventGalleryContext.event) {
      eventGalleryContext.removePhotoFromCart(photo);
    } else {
      // @ts-ignore
      findPhotosContext.removePhotoFromCart(photo);
    }
  };

  useEffect(() => {
    if (eventGalleryContext.curationMode) {
      fetch(
        `/api/v1/photos/${getPhoto.id}/is_hidden?user_id=${
          userId || eventGalleryContext.userId
        }`
      )
        .then(response => response.json())
        .then(data => setDefaultHidden(data.is_hidden));
    }
    if (canDownloadPhotos()) {
      fetch(
        `/api/v1/photos/${getPhoto.id}/is_saved?user_id=${
          userId || eventGalleryContext.userId
        }`
      )
        .then(response => response.json())
        .then(data => setPhotoSaved(data.is_saved));
    }
    if (eventGalleryContext.canManageTags) {
      getTags();
    }
    if (eventGalleryContext.userId || eventGalleryContext.guestUserId) {
      fetch(
        `/api/v1/photos/${getPhoto.id}/is_liked?user_id=${
          userId ||
          eventGalleryContext.userId ||
          eventGalleryContext.guestUserId
        }`
      )
        .then(response => response.json())
        .then(data => setLiked(data.is_liked));
    }

    // prefetch the next photo
    const nextPhoto = photos[photoIndex + 1];
    if (nextPhoto && window.gss?.albumV2Enabled) {
      // Prefetches the next photo, asynchronously,
      // so that when the user navigates to the next photo,
      // it loads faster
      precachePhoto(nextPhoto).catch(error => {
        gssErrorLog("Failed to precache photo", nextPhoto, error);
      });
    }

    if (window.gss?.albumV2Enabled) {
      if (!nextPhoto) {
        fetchNextBatchOfPhotos();
      }
    }
    gssLog("LightBox: Current photoIndex", photoIndex);
  }, [photoIndex]);

  const handleSave = () => {
    ajax
      .post(
        addParamToURL(
          withCartIdParam(
            `/api/v1/photos/${getPhoto.id}/save`,
            cartId,
            isEmbed
          ),
          "user_id",
          userId || eventGalleryContext.userId
        )
      )
      .then(() => {
        setPhotoSaved(true);
        // @ts-ignore
        toastr.success("Photo saved", null, { timeOut: 1000 });
      });
  };

  const handleUnsave = () => {
    ajax
      .delete(
        addParamToURL(
          withCartIdParam(`/api/v1/photos/${getPhoto.id}/unsave`, cartId),
          "user_id",
          userId || eventGalleryContext.userId
        )
      )
      .then(() => {
        setPhotoSaved(false);
        // @ts-ignore
        toastr.success("Photo unsaved!", null, { timeOut: 1000 });
      });
  };

  useEffect(() => {
    if (trackTelemetry)
      ahoy.track("photoView", {
        photo_id: getPhoto.id,
        album_id: getPhoto.album.id,
        event_id: getPhoto.album.eventId
      });

    document
      .getElementsByTagName("body")[0]
      .classList.add("modal-open", "egm-single-ph-open");
    // eventGalleryContext.canManageTags
    //   ? null
    //   : document.body.addEventListener("touchmove", preventDefault, {
    //       passive: false
    //     });

    return () => {
      document
        .getElementsByTagName("body")[0]
        .classList.remove("modal-open", "egm-single-ph-open");
      // eventGalleryContext.canManageTags
      //   ? null
      //   : document.body.removeEventListener("touchmove", preventDefault);
    };
  }, []);

  const getTags = () => {
    ajax
      .get(`/api/v1/content_tags/${getPhoto.id}`)
      .then(response => setMediaTags(response.body.appliedTags))
      .catch(error => {
        // @ts-ignore
        toastr.error("There was an error loading tags");
        console.error(error);
      });
  };

  const colorTheme = () =>
    primaryColor
      ? primaryColor
      : eventGalleryContext.pageConfiguration
      ? eventGalleryContext.pageConfiguration.primaryColor
      : "#259FA5";

  const colorThemeBorder = () =>
    photoSaved
      ? "gray"
      : primaryColor
      ? primaryColor
      : eventGalleryContext.pageConfiguration.primaryColor
      ? eventGalleryContext.pageConfiguration.primaryColor
      : "#259fa5";

  // Compute the processed colors once to ensure consistency
  const processedColorTheme = ContrastForegroundUtility.lbFgColor(colorTheme());
  const processedColorThemeBorder = ContrastForegroundUtility.lbFgColor(
    colorThemeBorder()
  );

  const handleCloseLightBox = () => {
    setCurationApiCalled(false);
    toggleLightBox(-1);
    eventGalleryContext.curationMode &&
      curationApiCalled &&
      eventGalleryContext.reloadAlbumPhotos(true);
  };

  const curatePhoto = async () => {
    const hiddenVal = curationApiCalled ? defaultHidden : getPhoto.hidden;
    const action = hiddenVal == true ? "unhide" : "hide";
    try {
      const res = await $.ajax({
        url: `/api/v1/photos/${action}`,
        type: "POST",
        data: {
          photo_ids: [getPhoto.id],
          event_id: getPhoto.album.eventId
        }
      });

      //@ts-ignore
      toastr.success("Successfully updated");
      setDefaultHidden(!hiddenVal);
    } catch (err) {
      //@ts-ignore
      toastr.error("Failed to update");
      console.error(err);
    }
  };

  const handleVideoLike = () => {
    if (eventGalleryContext.guestUserId || eventGalleryContext.userId) {
      handleLikeButtonClick(
        getPhoto.id,
        //@ts-ignore
        eventGalleryContext.userId || eventGalleryContext.guestUserId,
        "lightbox"
      );
      setLiked(!liked);
    } else {
      setEmailModal(true);
      trackGuestFavoriteEmailPrompted(getPhoto.id, "lightbox");
    }
  };

  const addLike = async (
    photoId: number,
    userId: number,
    source: string,
    userEmail: string | null
  ) => {
    const url = `${eventGalleryContext.addPhotoToFavoritesAPI}?photo_id=${photoId}&user_id=${userId}`;
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        }
      });
      if (userEmail) {
        eventGalleryContext.setGuestUserCart(
          userId,
          userEmail,
          eventGalleryContext.cart
        );
      }
      if (response.ok) {
        setLiked(true);
        trackAddToFavorite({ photoId, source });
        if (
          eventGalleryContext.filterSelections.favorite &&
          (eventGalleryContext.userId || eventGalleryContext.guestUserId)
        ) {
        }
      } else {
        const data = await response.json();
        if (response.status === 409) {
          //@ts-ignore
          toastr.error(data.error);
          trackAddToFavorite({ photoId, source });
          console.warn("Photo is already liked.");
        } else {
          console.error("Error adding like:", data.error);
        }
      }
    } catch (error) {
      console.error("Error adding like:", error);
    }
  };

  const removeLike = async (photoId: number, userId: number) => {
    const url = `${eventGalleryContext.removePhotoFromFavoritesAPI}?photo_id=${photoId}&user_id=${userId}`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json"
        }
      });

      if (response.ok) {
        setLiked(false);
      } else {
        const data = await response.json();
        console.error("Error removing like:", data.error);
      }
    } catch (error) {
      console.error("Error removing like:", error);
    }
  };

  const handleLikeButtonClick = async (
    photoId: number,
    userId: number,
    source: string,
    userEmail?: string
  ) => {
    if (!eventGalleryContext.guestUserId && !eventGalleryContext.userId) {
      await addLike(photoId, userId, source, userEmail ? userEmail : null);
    } else {
      if (liked) {
        removeLike(photoId, userId);
      } else {
        addLike(photoId, userId, source, userEmail ? userEmail : null);
      }
    }
  };

  const trackAddToFavorite = ({
    photoId,
    source
  }: {
    photoId: number;
    source: string;
  }) => {
    if (!eventGalleryContext.event) return;
    Telemetry.track("event-gallery:add-to-favorites", {
      eventId: eventGalleryContext.event.id,
      eventName: eventGalleryContext.event.name,
      photoId: photoId,
      source: source
    });
  };

  const trackGuestFavoriteEmailPrompted = (photoId: number, source: string) => {
    if (!eventGalleryContext.event) return;
    Telemetry.track("event-gallery:guest-favorites-email-prompted", {
      eventId: eventGalleryContext.event.id,
      eventName: eventGalleryContext.event.name,
      photoId: photoId,
      source: source
    });
  };

  const setGuestUser = (userId: number, userEmail: string) => {
    eventGalleryContext.setGuestUser?.(userId, userEmail);
    // For OrganizationEvent
    setGuestUserId?.(userId);
    const downloadUrl = `/end_user_content_downloads?photo_ids=${getPhoto.id}${
      userId ? `&user_id=${userId}` : ""
    }`;
    try {
      window.location.href = downloadUrl;
    } catch (error) {
      console.error("Failed to navigate to download URL", error);
      //@ts-ignore
      toastr.error("Failed to navigate to download URL. Please try again.");
    }
  };

  return (
    <>
      <div
        id="filter-dialog"
        className="modal fade in egm ns ns-modal ns-modal-small egm-lightbox"
        role="dialog"
      >
        <div className="modal-dialog filter-dialog-content">
          <div className="geo-img-popup" id="modal-mygallery">
            <div className="close-modal">
              <button
                type="button"
                className="close"
                aria-label="Close"
                onClick={() => handleCloseLightBox()}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
            <div className="modal-mygallery-ttl wtf">
              {showPrintLightboxHeader ? (
                <PrintLightboxHeader
                  photo={getPhoto}
                  primaryColor={colorTheme()}
                  addToCart={addToCart}
                  removeFromCart={removeFromCart}
                />
              ) : (
                <div className="mobile-header visible-xs-block">
                  <LightBoxHeader
                    photo={getPhoto}
                    primaryColor={primaryColor}
                    trackTelemetry={trackTelemetry}
                    parentName={parentName}
                    canShowGuestEmailModal={canShowGuestEmailModal}
                    collectEmailModal={collectEmailModal}
                    securedOrgView={securedOrgView}
                    userId={userId}
                  />
                </div>
              )}
              <div className="modal-body">
                <div className="dv-imgdetails">
                  <span className="align-helper" />
                  {photos[photoIndex] && photos[photoIndex].videoContent ? (
                    <div style={{ width: "calc(100vw - 60px)" }}>
                      <LightBoxVideo
                        video={photos[photoIndex] as unknown as Video}
                        onClickFavoriteIcon={handleVideoLike}
                        liked={liked}
                        hidden={defaultHidden}
                        showFavIcon={showFavIcon}
                        hasPrevImage={hasPrevImage()}
                        hasNextImage={hasNextImage()}
                        resetLightBoxImage={resetLightBoxImage}
                        photoIndex={photoIndex}
                      />
                    </div>
                  ) : (
                    <LightboxImage
                      photo={getPhoto}
                      hidden={defaultHidden}
                      setEmailModal={setEmailModal}
                      liked={liked}
                      setLiked={setLiked}
                      handleLikeButtonClick={handleLikeButtonClick}
                      showFavIcon={showFavIcon}
                      trackGuestFavoriteEmailPrompted={
                        trackGuestFavoriteEmailPrompted
                      }
                      hasPrevImage={hasPrevImage()}
                      hasNextImage={hasNextImage()}
                      resetLightBoxImage={resetLightBoxImage}
                      photoIndex={photoIndex}
                    />
                  )}
                </div>
              </div>
              <div>
                <LightBoxFooter
                  photo={getPhoto}
                  isAuthorizedForDirectDownloads={
                    isAuthorizedForDirectDownloads
                  }
                  primaryColor={primaryColor}
                  processedColorTheme={processedColorTheme}
                  processedColorThemeBorder={processedColorThemeBorder}
                  userId={userId}
                  photoSaved={photoSaved}
                  handleSave={handleSave}
                  handleUnsave={handleUnsave}
                  curatePhoto={curatePhoto}
                  defaultHidden={defaultHidden}
                  setCurationApiCalled={setCurationApiCalled}
                  isEmbed={isEmbed}
                  cartId={cartId}
                  eventClickable={eventClickable}
                  secureAccessToken={secureAccessToken}
                />
              </div>

              {/* Print Products Section for Mobile */}
              {eventGalleryContext.printProductsflow?.() && (
                <div className="mobile-print-products-section">
                  <LightBoxPrintProducts
                    photo={getPhoto}
                    event={eventGalleryContext.event}
                    cartId={cartId}
                    cart={eventGalleryContext.cart}
                    primaryColor={colorTheme()}
                    updateCartFromApiResponse={
                      eventGalleryContext.updateCartFromApiResponse
                    }
                    onCartStatusChange={(hasItems: boolean) => {}}
                    canShowEmailPopup={eventGalleryContext.canShowEmailPopup}
                    showSaveCartModal={
                      eventGalleryContext.showSaveCartModalWithItem
                    }
                    isEmbed={eventGalleryContext.isEmbed}
                  />
                </div>
              )}

              {eventGalleryContext.canManageTags &&
                !eventGalleryContext.printProductsflow?.() && (
                  <>
                    <TagsSection
                      photoId={[getPhoto.id]}
                      appliedTags={mediaTags}
                      contentTagsAPI={"/api/v1/content_tags"}
                      colorStyling={processedColorTheme}
                    />
                  </>
                )}
            </div>
          </div>
        </div>
      </div>
      {emailModal && (
        <EmailPopup
          closePopup={() => {
            // add class to handle overlapping issue on ios devices
            document.body.classList.remove("fav-email-popup-open");
            setEmailModal(false);
          }}
          handleLikeButtonClick={handleLikeButtonClick}
          closeLightBox={() => {
            setEmailModal(false);
          }}
          photoId={getPhoto.id}
        />
      )}

      {showDownloadWithEmailModal && (
        <DownloadWithEmailModal
          closeModal={collectEmailModal}
          registerGuestUserAPI={
            eventGalleryContext.registerGuestUserAPI || registerGuestUserAPI
          }
          eventId={getPhoto.album.eventId}
          setGuestUser={(userId: number, userEmail: string) => {
            setGuestUser(userId, userEmail);
          }}
        />
      )}
    </>
  );
};

export default LightBox;
